import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, LayoutGrid, Loader2, Search } from 'lucide-react';
import { useTranslation } from '@aimsos/i18n';
import { useStickyScroll } from '@aimsos/hooks';
import { Pagination, StatusMessage } from '@aimsos/ui';

import {
  CreateWorkspaceModal,
  WorkspaceGridCard,
  WorkspacesListHeader,
  WorkspacesTable,
} from '../components';
import { useWorkspaces } from '../context';

const LIST_PAGE_SIZE_OPTIONS = [10, 15, 20, 50];

const WorkspacesListPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('workspaces');
  const {
    workspaces,
    listLoading,
    listError,
    meta,
    setPage,
    pageSize,
    setPageSize,
    listSearchKeyword,
    setListSearchKeyword,
    refetchWorkspaces,
  } = useWorkspaces();

  const [view, setView] = useState('grid');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [listScrollHover, setListScrollHover] = useState(false);

  const tableHeaders = useMemo(
    () => ({
      name: t('list.table.columns.name'),
      owner: t('list.table.columns.owner'),
      status: t('list.table.columns.status'),
      updated: t('list.table.columns.updated'),
      members: t('list.table.columns.members'),
    }),
    [t],
  );

  const rangeLabel = useMemo(() => {
    const total = meta.total_items;
    if (total === 0 || workspaces.length === 0) return '0-0';
    const start = (meta.current_page - 1) * meta.per_page + 1;
    const end = start + workspaces.length - 1;
    return `${start}-${end}`;
  }, [meta.current_page, meta.per_page, meta.total_items, workspaces.length]);

  const handleSelectWorkspace = useCallback(
    (workspace) => {
      navigate(`/workspaces/${workspace.id}`);
    },
    [navigate],
  );

  const handlePrevPage = useCallback(() => {
    setPage((p) => Math.max(1, p - 1));
  }, [setPage]);

  const handleNextPage = useCallback(() => {
    setPage((p) => p + 1);
  }, [setPage]);

  const isInitialLoad =
    listLoading && workspaces.length === 0 && !listError && !listSearchKeyword.trim();

  const handleCreateClick = useCallback(() => {
    setCreateModalOpen(true);
  }, []);

  const handleCreateModalClose = useCallback(() => {
    setCreateModalOpen(false);
  }, []);

  const handleWorkspaceCreated = useCallback(
    (workspaceId) => {
      refetchWorkspaces();
      if (workspaceId) {
        navigate(`/workspaces/${workspaceId}/overview`);
      }
    },
    [navigate, refetchWorkspaces],
  );

  const showListPagination = !isInitialLoad && !listError && meta.total_items > 0;

  // `Pagination` composes "<range> of <total> items" in English by default, so
  // the bar has to be told its copy or a Spanish page prints an English footer.
  const paginationLabels = useMemo(
    () => ({
      rowsPerPage: t('list.pagination.rowsPerPage'),
      items: t('list.pagination.items', { range: rangeLabel, total: meta.total_items }),
      previousPage: t('list.pagination.previousPage'),
      nextPage: t('list.pagination.nextPage'),
    }),
    [t, rangeLabel, meta.total_items],
  );

  const scrollRef = useRef(null);
  const { isScrolled, filtersVisible, onMouseEnter, onMouseLeave } = useStickyScroll(scrollRef);
  // At rest the toolbar is already on screen, so the hover pair is armed only
  // while compressed.
  const showToolbar = !isScrolled || filtersVisible;

  // Results only. The header band is rendered by the page and stays put through
  // every one of these states — previously it lived inside this function, so a
  // load or an error took the title, the Create button and the search with it.
  const renderResults = () => {
    if (isInitialLoad || listLoading) {
      return (
        <StatusMessage
          icon={Loader2}
          iconClassName="animate-spin"
          title={t('list.table.loading')}
          subtitle={t('list.table.loadingDescription')}
        />
      );
    }

    if (listError) {
      return (
        <StatusMessage icon={AlertTriangle} title={t('list.table.error')} subtitle={listError} />
      );
    }

    if (workspaces.length === 0) {
      return listSearchKeyword.trim() ? (
        <StatusMessage
          icon={Search}
          title={t('list.table.noResults')}
          subtitle={t('list.table.noResultsFor', { term: listSearchKeyword.trim() })}
        />
      ) : (
        <StatusMessage
          icon={LayoutGrid}
          title={t('list.table.noWorkspaces')}
          subtitle={t('list.table.noWorkspacesDescription')}
        />
      );
    }

    return view === 'grid' ? (
      <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-2 md:gap-5 lg:grid-cols-3 lg:gap-5 [&>*]:min-w-0">
        {workspaces.map((workspace) => (
          <WorkspaceGridCard
            key={workspace.id}
            workspace={workspace}
            onViewDetails={handleSelectWorkspace}
          />
        ))}
      </div>
    ) : (
      <WorkspacesTable
        workspaces={workspaces}
        headers={tableHeaders}
        onSelect={handleSelectWorkspace}
      />
    );
  };

  return (
    // Root owns NO scroll: two scrollers and `position: sticky` resolves against
    // the wrong one. See docs/architecture/list-page/.
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-surface-base">
      <div
        ref={scrollRef}
        data-workspaces-list-scroll
        className="scrollbar-aimsos-auto min-h-0 flex-1 overflow-y-auto"
        data-scrollbar-hover={listScrollHover ? 'true' : undefined}
        onMouseEnter={() => setListScrollHover(true)}
        onMouseLeave={() => setListScrollHover(false)}
      >
        {/* `min-h-full` is what lets the footer's `mt-auto` reach the bottom on a
            short list instead of sitting under the last card. */}
        <div className="flex min-h-full flex-col">
          <WorkspacesListHeader
            searchValue={listSearchKeyword}
            onSearchChange={setListSearchKeyword}
            onSearchClear={() => setListSearchKeyword('')}
            view={view}
            onViewChange={setView}
            onCreateClick={handleCreateClick}
            isScrolled={isScrolled}
            showToolbar={showToolbar}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          />

          {/* No bottom padding while the pagination shows: a sticky element stays
              pinned only while its flow position is off screen, so padding here
              would un-pin it by exactly that much on the last scroll step. */}
          <div
            className={['flex min-w-0 flex-1 flex-col gap-4 px-6', showListPagination ? '' : 'pb-6']
              .filter(Boolean)
              .join(' ')}
          >
            <div className="min-w-0">{renderResults()}</div>

            {showListPagination ? (
              <div className="sticky bottom-0 z-10 mt-auto border-t border-edge bg-surface-base pb-6 pt-3">
                <Pagination
                  rangeLabel={rangeLabel}
                  totalItems={meta.total_items}
                  currentPage={meta.current_page}
                  totalPages={meta.total_pages}
                  hasPrevious={meta.has_previous}
                  hasNext={meta.has_next}
                  perPage={pageSize}
                  pageSizeOptions={LIST_PAGE_SIZE_OPTIONS}
                  onPrevPage={handlePrevPage}
                  onNextPage={handleNextPage}
                  onPageSizeChange={setPageSize}
                  disabled={listLoading}
                  labels={paginationLabels}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Overlays are siblings of the scroll container, never inside it. */}
      <CreateWorkspaceModal
        isOpen={createModalOpen}
        onClose={handleCreateModalClose}
        onCreated={handleWorkspaceCreated}
      />
    </div>
  );
};

export default WorkspacesListPage;
